import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';

const ParentTimetable = ({ students, onBack }) => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentTimetables, setStudentTimetables] = useState([]);
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'daily'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [subjectColors, setSubjectColors] = useState({});

  // Initialize with first student
  useEffect(() => {
    if (students && students.length > 0 && !selectedStudent) {
      setSelectedStudent(students[0]);
    }
  }, [students]);

  // Fetch subject colors from backend
  useEffect(() => {
    const fetchSubjectColors = async () => {
      try {
        const response = await fetch(buildApiUrl('/admin-subjects'));
        const data = await response.json();
        const colorMap = {};
        if (data && Array.isArray(data)) {
          data.forEach(subject => {
            colorMap[subject.name || subject.subject] = subject.color || '#6B7280';
          });
        }
        setSubjectColors(colorMap);
      } catch (error) {
        console.error('Error fetching subject colors:', error);
      }
    };
    fetchSubjectColors();
  }, []);

  // Subject color mapping - uses colors from backend (School Manager → Timetable)
  const getSubjectColor = (subjectName) => {
    if (!subjectName) return '#E5E7EB';
    
    // Use color from backend, fallback to default gray
    return subjectColors[subjectName] || '#6B7280';
  };

  // Get period display color
  const getPeriodDisplayColor = (period, assignment) => {
    if (!period) return '#E5E7EB';
    
    if (period.type !== 'lesson') {
      return period.color || '#F59E0B';
    }
    
    if (assignment?.subject) {
      return getSubjectColor(assignment.subject);
    }
    
    return period.color || '#3B82F6';
  };

  // Helper functions
  const getCurrentDisplayDate = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    
    if (dayOfWeek === 5 || dayOfWeek === 6) {
      const sunday = new Date(today);
      sunday.setDate(today.getDate() + (7 - dayOfWeek));
      return sunday;
    }
    return today;
  };

  const getDayName = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  // Fetch timetables for selected student
  useEffect(() => {
    if (!selectedStudent) return;

    const fetchTimetables = async () => {
      setLoading(true);
      try {
        const response = await fetch(buildApiUrl(`/student-timetables/${selectedStudent.school_id}`));
        const data = await response.json();
        
        setStudentTimetables(data.timetables || []);
        
        if (data.timetables && data.timetables.length > 0) {
          setSelectedTimetable(data.timetables[0]);
        } else {
          setSelectedTimetable(null);
        }
      } catch (error) {
        console.error('Error fetching timetables:', error);
        setStudentTimetables([]);
        setSelectedTimetable(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTimetables();
  }, [selectedStudent]);

  // Initialize date
  useEffect(() => {
    setSelectedDate(getCurrentDisplayDate());
  }, []);

  if (!students || students.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👨‍👩‍👧‍👦</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Students Found</h3>
            <p className="text-gray-600">No students are linked to your account</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <span className="text-2xl">←</span>
            </button>
          )}
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <span className="text-blue-600 text-xl">📅</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Student Timetable</h2>
            <p className="text-gray-600">View your children's class schedules</p>
          </div>
        </div>

        {/* Student Selector */}
        {students.length > 1 && (
          <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
            <select
              value={selectedStudent?.school_id || ''}
              onChange={(e) => {
                const student = students.find(s => s.school_id === e.target.value);
                setSelectedStudent(student);
              }}
              className="w-full md:w-auto border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {students.map((student) => (
                <option key={student.school_id} value={student.school_id}>
                  {student.full_name} - Form {student.form}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-2xl">📅</span>
            </div>
            <p className="text-gray-500 text-lg">Loading timetable...</p>
          </div>
        ) : studentTimetables.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📅</span>
            </div>
            <p className="text-gray-500 text-lg">No timetable assigned yet</p>
            <p className="text-gray-400 text-sm mt-2">
              Contact the school if you believe this is an error
            </p>
            {selectedStudent && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg inline-block">
                <p className="text-blue-700 text-sm">
                  👤 Student: <strong>{selectedStudent.full_name}</strong><br/>
                  📚 Form: <strong>{selectedStudent.form}</strong>
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* View Mode Toggle */}
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">View:</span>
                  <div className="flex bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setViewMode('weekly')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        viewMode === 'weekly' 
                          ? 'bg-blue-500 text-white' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      📅 Weekly
                    </button>
                    <button
                      onClick={() => setViewMode('daily')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        viewMode === 'daily' 
                          ? 'bg-blue-500 text-white' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      📋 Daily
                    </button>
                  </div>
                </div>

                {/* Daily View Date Navigation */}
                {viewMode === 'daily' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigateDay(-1)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      ←
                    </button>
                    <span className="text-sm font-medium text-gray-900 min-w-max">
                      {formatDate(selectedDate)}
                    </span>
                    <button
                      onClick={() => navigateDay(1)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Timetable Display */}
            {selectedTimetable && (
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="p-6">
                  {viewMode === 'weekly' ? (
                    /* Weekly View */
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map(day => (
                          <div key={day} className="bg-gray-50 rounded-lg p-3">
                            <h4 className="font-semibold text-gray-900 mb-2 text-center text-sm">{day}</h4>
                            <div className="space-y-1">
                              {selectedTimetable.days?.[day]?.map((assignment, index) => (
                                <div 
                                  key={index}
                                  className="p-2 rounded-md text-white text-xs"
                                  style={{ backgroundColor: getPeriodDisplayColor(assignment.period, assignment) }}
                                >
                                  <div className="font-medium">{assignment.subject || assignment.period?.name}</div>
                                  <div className="text-xs opacity-90">
                                    {assignment.period?.startTime} - {assignment.period?.endTime}
                                  </div>
                                  {assignment.teacher && (
                                    <div className="text-xs opacity-90">
                                      👨‍🏫 {assignment.teacher}
                                    </div>
                                  )}
                                  {assignment.room && (
                                    <div className="text-xs opacity-90">
                                      🏫 {assignment.room}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {(!selectedTimetable.days?.[day] || selectedTimetable.days[day].length === 0) && (
                                <div className="text-center text-gray-400 py-6 text-xs">
                                  No periods
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Weekly Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {Object.values(selectedTimetable.days || {}).flat().filter(a => a.period?.type === 'lesson').length}
                          </div>
                          <div className="text-sm text-blue-700">Total Lessons</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {(() => {
                              const subjects = new Set();
                              Object.values(selectedTimetable.days || {}).flat().forEach(a => {
                                if (a.subject) subjects.add(a.subject);
                              });
                              return subjects.size;
                            })()}
                          </div>
                          <div className="text-sm text-green-700">Different Subjects</div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">
                            {(() => {
                              const teachers = new Set();
                              Object.values(selectedTimetable.days || {}).flat().forEach(a => {
                                if (a.teacher) teachers.add(a.teacher);
                              });
                              return teachers.size;
                            })()}
                          </div>
                          <div className="text-sm text-purple-700">Different Teachers</div>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <div className="text-2xl font-bold text-yellow-600">
                            {Object.values(selectedTimetable.days || {}).flat().filter(a => a.period?.type === 'break').length}
                          </div>
                          <div className="text-sm text-yellow-700">Break Periods</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Daily View */
                    <div className="space-y-6">
                      {(() => {
                        const dayName = getDayName(selectedDate);
                        const daySchedule = selectedTimetable.days?.[dayName] || [];
                        
                        return (
                          <div>
                            <div className="mb-4">
                              <h4 className="text-lg font-semibold text-gray-900">
                                Schedule for {dayName}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>

                            {daySchedule.length === 0 ? (
                              <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <span className="text-2xl">🏖️</span>
                                </div>
                                <p className="text-gray-500 text-lg">No classes scheduled</p>
                                <p className="text-gray-400 text-sm mt-2">
                                  Enjoy your free day!
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {daySchedule.map((assignment, index) => (
                                  <div 
                                    key={index}
                                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                          <div 
                                            className="w-4 h-4 rounded-full"
                                            style={{ backgroundColor: getPeriodDisplayColor(assignment.period, assignment) }}
                                          ></div>
                                          <h5 className="font-semibold text-gray-900">
                                            {assignment.subject || assignment.period?.name}
                                          </h5>
                                          <span className="text-sm text-gray-500 font-mono">
                                            {assignment.period?.startTime} - {assignment.period?.endTime}
                                          </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                          {assignment.subject && (
                                            <div className="flex items-center gap-2">
                                              <span className="text-gray-500">📚</span>
                                              <span className="font-medium">{assignment.subject}</span>
                                            </div>
                                          )}
                                          {assignment.teacher && (
                                            <div className="flex items-center gap-2">
                                              <span className="text-gray-500">👨‍🏫</span>
                                              <span>{assignment.teacher}</span>
                                            </div>
                                          )}
                                          {assignment.room && (
                                            <div className="flex items-center gap-2">
                                              <span className="text-gray-500">🏫</span>
                                              <span>{assignment.room}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Daily Stats */}
                            {daySchedule.length > 0 && (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                  <div className="text-2xl font-bold text-blue-600">
                                    {daySchedule.filter(a => a.period?.type === 'lesson').length}
                                  </div>
                                  <div className="text-sm text-blue-700">Lessons Today</div>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                  <div className="text-2xl font-bold text-yellow-600">
                                    {daySchedule.filter(a => a.period?.type === 'break').length}
                                  </div>
                                  <div className="text-sm text-yellow-700">Break Periods</div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                  <div className="text-2xl font-bold text-green-600">
                                    {new Set(daySchedule.map(a => a.subject).filter(Boolean)).size}
                                  </div>
                                  <div className="text-sm text-green-700">Different Subjects</div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentTimetable;
